import { nanoid } from "nanoid";

/**
 * Shared constants for the ERP system
 */

/**
 * Constant representing products that don't belong to any specific product group.
 * When this value is selected in the UI, it should be converted to null in the database
 * to properly work with rebate calculation logic for "other products" rules.
 */
export const ALL_OTHER_PRODUCTS = "All Other Products" as const;

/**
 * Constant representing non rebatable products.
 * When this value is selected in the UI, it should be converted to null in the database
 * to properly work with rebate calculation logic for "non rebatable products" rules.
 */
export const NON_REBATABLE_PRODUCTS = "Non Rebatable Products" as const;

/**
 * Base prefix for documents created from email body content.
 * These are internal documents for processing and should not be displayed as attachments.
 */
export const EMAIL_BODY_DOCUMENT_PREFIX = "__docified_email_body_" as const;

/**
 * Generates a unique filename for email body documents.
 * Uses a nanoid suffix to ensure uniqueness.
 */
export function generateEmailBodyDocumentFilename(): string {
  return `${EMAIL_BODY_DOCUMENT_PREFIX}${nanoid()}.html`;
}

/**
 * Checks if a filename represents an internal email body document
 * that should not be displayed as an attachment.
 */
export function isEmailBodyDocument(
  fileName: string | null | undefined,
): boolean {
  return fileName?.startsWith(EMAIL_BODY_DOCUMENT_PREFIX) ?? false;
}

/**
 * Map of ISO 639-3 language codes to ISO 639-1 codes (where available)
 * Covers all 186 languages supported by the franc package
 */
export const ISO_639_3_TO_1: Record<string, string> = {
  cmn: "zh",
  spa: "es",
  eng: "en",
  rus: "ru",
  arb: "ar",
  ben: "bn",
  hin: "hi",
  por: "pt",
  ind: "id",
  jpn: "ja",
  fra: "fr",
  deu: "de",
  jav: "jv",
  kor: "ko",
  tel: "te",
  vie: "vi",
  mar: "mr",
  ita: "it",
  tam: "ta",
  tur: "tr",
  urd: "ur",
  guj: "gu",
  pol: "pl",
  ukr: "uk",
  kan: "kn",
  mai: "mai",
  mal: "ml",
  pes: "fa",
  mya: "my",
  swh: "sw",
  sun: "su",
  ron: "ro",
  pan: "pa",
  bho: "bho",
  amh: "am",
  hau: "ha",
  fuv: "ff",
  bos: "bs",
  hrv: "hr",
  nld: "nl",
  srp: "sr",
  tha: "th",
  ckb: "ku",
  yor: "yo",
  uzn: "uz",
  zlm: "ms",
  ibo: "ig",
  npi: "ne",
  ceb: "ceb",
  skr: "skr",
  tgl: "tl",
  hun: "hu",
  azj: "az",
  sin: "si",
  koi: "koi",
  ell: "el",
  ces: "cs",
  mag: "mag",
  run: "rn",
  bel: "be",
  plt: "mg",
  qug: "qug",
  mad: "mad",
  nya: "ny",
  zyb: "za",
  pbu: "ps",
  kin: "rw",
  zul: "zu",
  bul: "bg",
  swe: "sv",
  lin: "ln",
  som: "so",
  hms: "hms",
  hnj: "hnj",
  ilo: "ilo",
  kaz: "kk",
  uig: "ug",
  hat: "ht",
  khm: "km",
  prs: "prs",
  hil: "hil",
  sna: "sn",
  tat: "tt",
  xho: "xh",
  hye: "hy",
  min: "min",
  afr: "af",
  lua: "lua",
  sat: "sat",
  bod: "bo",
  tir: "ti",
  fin: "fi",
  slk: "sk",
  tuk: "tk",
  dan: "da",
  nob: "nb",
  suk: "suk",
  als: "sq",
  sag: "sg",
  nno: "nn",
  heb: "he",
  mos: "mos",
  tgk: "tg",
  cat: "ca",
  sot: "st",
  kat: "ka",
  bcl: "bcl",
  glg: "gl",
  lao: "lo",
  lit: "lt",
  umb: "umb",
  tsn: "tn",
  vec: "vec",
  nso: "nso",
  ban: "ban",
  bug: "bug",
  knc: "knc",
  kng: "kg",
  ibb: "ibb",
  lug: "lg",
  ace: "ace",
  bam: "bm",
  tzm: "tzm",
  ydd: "yi",
  kmb: "kmb",
  lun: "lun",
  shn: "shn",
  war: "war",
  dyu: "dyu",
  wol: "wo",
  kir: "ky",
  nds: "nds",
  fuf: "fuf",
  mkd: "mk",
  vmw: "vmw",
  zgh: "zgh",
  ewe: "ee",
  khk: "mn",
  slv: "sl",
  ayr: "ay",
  bem: "bem",
  emk: "emk",
  bci: "bci",
  bum: "bum",
  epo: "eo",
  pam: "pam",
  tiv: "tiv",
  tpi: "tpi",
  ven: "ve",
  ssw: "ss",
  nyn: "nyn",
  kbd: "kbd",
  iii: "ii",
  yao: "yao",
  lvs: "lv",
  quz: "quz",
  src: "sc",
  rup: "rup",
  sco: "sco",
  tso: "ts",
  men: "men",
  fon: "fon",
  nhn: "nhn",
  dip: "dip",
  kde: "kde",
  kbp: "kbp",
  tem: "tem",
  toi: "toi",
  ekk: "et",
  snk: "snk",
  cjk: "cjk",
  ada: "ada",
  aii: "aii",
  quy: "quy",
  rmn: "rmn",
  bin: "bin",
  gaa: "gaa",
  ndo: "ng",
  zho: "zh",
  msa: "ms",
  nor: "no",
  ara: "ar",
};

/**
 * Map of language codes to English display names
 * Covers all 186 languages supported by the franc package
 */
export const ISO_639_1_LANGUAGE_NAMES: Record<string, string> = {
  zh: "Chinese",
  es: "Spanish",
  en: "English",
  ru: "Russian",
  ar: "Arabic",
  bn: "Bengali",
  hi: "Hindi",
  pt: "Portuguese",
  id: "Indonesian",
  ja: "Japanese",
  fr: "French",
  de: "German",
  jv: "Javanese",
  ko: "Korean",
  te: "Telugu",
  vi: "Vietnamese",
  mr: "Marathi",
  it: "Italian",
  ta: "Tamil",
  tr: "Turkish",
  ur: "Urdu",
  gu: "Gujarati",
  pl: "Polish",
  uk: "Ukrainian",
  kn: "Kannada",
  mai: "Maithili",
  ml: "Malayalam",
  fa: "Persian",
  my: "Burmese",
  sw: "Swahili",
  su: "Sundanese",
  ro: "Romanian",
  pa: "Punjabi",
  bho: "Bhojpuri",
  am: "Amharic",
  ha: "Hausa",
  ff: "Fulfulde",
  bs: "Bosnian",
  hr: "Croatian",
  nl: "Dutch",
  sr: "Serbian",
  th: "Thai",
  ku: "Kurdish",
  yo: "Yoruba",
  uz: "Uzbek",
  ms: "Malay",
  ig: "Igbo",
  ne: "Nepali",
  ceb: "Cebuano",
  skr: "Saraiki",
  tl: "Tagalog",
  hu: "Hungarian",
  az: "Azerbaijani",
  si: "Sinhala",
  koi: "Komi-Permyak",
  el: "Greek",
  cs: "Czech",
  mag: "Magahi",
  rn: "Rundi",
  be: "Belarusian",
  mg: "Malagasy",
  qug: "Chimborazo Highland Quichua",
  mad: "Madurese",
  ny: "Nyanja",
  za: "Zhuang",
  ps: "Pashto",
  rw: "Kinyarwanda",
  zu: "Zulu",
  bg: "Bulgarian",
  sv: "Swedish",
  ln: "Lingala",
  so: "Somali",
  hms: "Southern Qiandong Miao",
  hnj: "Hmong Njua",
  ilo: "Iloko",
  kk: "Kazakh",
  ug: "Uyghur",
  ht: "Haitian Creole",
  km: "Khmer",
  prs: "Dari",
  hil: "Hiligaynon",
  sn: "Shona",
  tt: "Tatar",
  xh: "Xhosa",
  hy: "Armenian",
  min: "Minangkabau",
  af: "Afrikaans",
  lua: "Luba-Lulua",
  sat: "Santali",
  bo: "Tibetan",
  ti: "Tigrinya",
  fi: "Finnish",
  sk: "Slovak",
  tk: "Turkmen",
  da: "Danish",
  nb: "Norwegian Bokmål",
  suk: "Sukuma",
  sq: "Albanian",
  sg: "Sango",
  nn: "Norwegian Nynorsk",
  he: "Hebrew",
  mos: "Mossi",
  tg: "Tajik",
  ca: "Catalan",
  st: "Southern Sotho",
  ka: "Georgian",
  bcl: "Central Bikol",
  gl: "Galician",
  lo: "Lao",
  lt: "Lithuanian",
  umb: "Umbundu",
  tn: "Tswana",
  vec: "Venetian",
  nso: "Northern Sotho",
  ban: "Balinese",
  bug: "Buginese",
  knc: "Central Kanuri",
  kg: "Kongo",
  ibb: "Ibibio",
  lg: "Ganda",
  ace: "Achinese",
  bm: "Bambara",
  tzm: "Central Atlas Tamazight",
  yi: "Yiddish",
  kmb: "Kimbundu",
  lun: "Lunda",
  shn: "Shan",
  war: "Waray",
  dyu: "Dyula",
  wo: "Wolof",
  ky: "Kyrgyz",
  nds: "Low German",
  fuf: "Pular",
  mk: "Macedonian",
  vmw: "Makhuwa",
  zgh: "Standard Moroccan Tamazight",
  ee: "Ewe",
  mn: "Mongolian",
  sl: "Slovenian",
  ay: "Aymara",
  bem: "Bemba",
  emk: "Eastern Maninkakan",
  bci: "Baoulé",
  bum: "Bulu",
  eo: "Esperanto",
  pam: "Pampanga",
  tiv: "Tiv",
  tpi: "Tok Pisin",
  ve: "Venda",
  ss: "Swati",
  nyn: "Nyankole",
  kbd: "Kabardian",
  ii: "Sichuan Yi",
  yao: "Yao",
  lv: "Latvian",
  quz: "Cusco Quechua",
  sc: "Sardinian",
  rup: "Aromanian",
  sco: "Scots",
  ts: "Tsonga",
  men: "Mende",
  fon: "Fon",
  nhn: "Central Nahuatl",
  dip: "Northeastern Dinka",
  kde: "Makonde",
  kbp: "Kabiyè",
  tem: "Timne",
  toi: "Tonga",
  et: "Estonian",
  snk: "Soninke",
  cjk: "Chokwe",
  ada: "Adangme",
  aii: "Assyrian Neo-Aramaic",
  quy: "Ayacucho Quechua",
  rmn: "Balkan Romani",
  bin: "Edo",
  gaa: "Ga",
  ng: "Ndonga",
  no: "Norwegian",
};

/**
 * Get the English display name for an ISO 639-1 language code
 * Falls back to uppercase code if not found
 */
export function getLanguageDisplayName(
  isoCode: string | null | undefined,
): string {
  if (!isoCode) return "Unknown";

  const normalized = isoCode.trim().toLowerCase();
  return ISO_639_1_LANGUAGE_NAMES[normalized] ?? normalized.toUpperCase();
}
